import React, {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { useId } from '@react-aria/utils'
import classnames from 'classnames'

import { TransitionalPlusMinusIcon } from '../../Icons/TransitionalPlusMinus/TransitionalPlusMinus'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from '../Accordion.module.scss'
import type { AccordionItemBaseProps } from '../AccordionItem'

export const AccordionContentDrawer = forwardRef<
  HTMLElement,
  AccordionContentDrawerProps
>(
  (
    {
      tag = 'section',
      navItem,
      ariaLabel,
      label,
      content,
      className,
      animatesOpen = true,
      callback,
      isOpen,
      setIsOpen,
      disabled,
      ...rest
    },
    ref
  ) => {
    const accordionItemBody = useRef<HTMLDivElement>(null)
    const initialRender = useRef(true)
    const buttonId = useId()
    const panelId = useId()

    const handleAccordionMaxHeight = useCallback(
      (open) => {
        if (animatesOpen && accordionItemBody.current) {
          accordionItemBody.current.style.maxHeight =
            open && !initialRender.current
              ? `${accordionItemBody.current.scrollHeight}px`
              : ''
        }
      },
      [animatesOpen]
    )

    const handleClickInternals = useCallback(
      (isOpen: boolean) => {
        handleAccordionMaxHeight(isOpen)
        callback && callback(isOpen)
      },
      [callback, handleAccordionMaxHeight]
    )

    const handleClick = () => {
      setIsOpen
        ? setIsOpen((prevIsOpen) => {
            const newIsOpen = !prevIsOpen
            handleClickInternals(newIsOpen)
            return newIsOpen
          })
        : handleClickInternals(!isOpen)
    }

    useEffect(() => {
      const accordionContent = accordionItemBody?.current

      const transitionHandler = (event: TransitionEvent) => {
        const element = event.target as HTMLElement

        if (event.propertyName === 'max-height' && element.scrollHeight > 0) {
          element.style.maxHeight = ''
        }
      }

      if (accordionContent) {
        accordionContent.addEventListener('transitionend', transitionHandler)
      }

      return () => {
        if (accordionContent) {
          accordionContent.removeEventListener(
            'transitionend',
            transitionHandler
          )
        }
      }
    }, [])

    const updateAccordionHeight = useCallback(
      (isOpen) => {
        if (!initialRender.current) {
          handleAccordionMaxHeight(isOpen)
        } else {
          initialRender.current = false
        }
      },
      [handleAccordionMaxHeight]
    )

    useEffect(() => {
      setIsOpen
        ? setIsOpen(() => {
            updateAccordionHeight(isOpen)
            return isOpen
          })
        : updateAccordionHeight(isOpen)
    }, [isOpen, setIsOpen, handleAccordionMaxHeight, updateAccordionHeight])

    useEffect(() => {
      if (disabled) {
        setIsOpen
          ? setIsOpen(() => {
              handleAccordionMaxHeight(false)
              return false
            })
          : handleAccordionMaxHeight(false)
      }
    }, [disabled, setIsOpen, handleAccordionMaxHeight])

    const props = {
      className: classnames(
        styles.accordionItem,
        isOpen && styles.accordionItemShow,
        className
      ),
      'data-testid': 'accordion-item',
      ref,
      ...rest,
    }

    const itemChildren = (
      <>
        <TypographyV2
          tag="h3"
          className={styles.accordionItemHeadingButtonWrapper}
          textStyle="sb-text-interface-heading-large"
        >
          <button
            id={buttonId}
            aria-expanded={isOpen}
            aria-controls={panelId}
            aria-label={ariaLabel}
            className={classnames(
              styles.accordionItemHeadingButton,
              navItem && styles.accordionItemHeadingButtonNav
            )}
            onClick={handleClick}
            type="button"
            data-testid="accordion-item-button"
          >
            <TypographyV2
              tag="span"
              textStyle="sb-text-interface-body-default-strong"
            >
              {label}
            </TypographyV2>
            <TransitionalPlusMinusIcon
              aria-hidden={true}
              toggle={isOpen}
              className={styles.accordionItemHeadingButtonIcon}
              width={16}
              height={16}
            />
          </button>
        </TypographyV2>
        <section
          id={panelId}
          ref={accordionItemBody}
          aria-hidden={!isOpen}
          aria-labelledby={buttonId}
          className={classnames(
            styles.accordionItemBody,
            !animatesOpen && styles.accordionItemBodyNoAnimation,
            isOpen && styles.accordionItemBodyShow
          )}
          data-testid="accordion-item-body"
        >
          {content}
        </section>
      </>
    )

    return createElement(tag, props, itemChildren)
  }
)

export type AccordionContentDrawerProps = AccordionItemBaseProps & {
  /**
   * boolean to determine whether the accordion drawer is opened or closed
   */
  isOpen: boolean
  /**
   * Optional setState function as returned from a useState hook to set the value of isOpen, as in AccordionItem
   */
  setIsOpen?: (value: boolean | ((isOpen: boolean) => boolean)) => void
}
